
























































































































import Vue from 'vue';
import store from '@/store';
import {
  ManualGroupBuilder,
  GroupBuilderKind,
  WorkflowStep
} from '@/store/getStarted';

import { MemberRole } from '@/models';
import * as Utils from '@/utils';
import * as InputValidation from '@/mixins/inputValidation';

import GetStartedNav from '@/components/getStarted/GetStartedNav.vue';
import GetStartedNavNextButton from '@/components/getStarted/GetStartedNavNextButton.vue';
import RoleSelectorPills, {
  PillConfig
} from '@/components/RoleSelectorPills.vue';
import PeerGroupRoleInfo from '@/components/info/PeerGroupRoleInfo.vue';

const defaultRoleConfig: PillConfig[] = [
  {
    role: MemberRole.STUDENT,
    roleName: MemberRole.STUDENT
  },
  {
    role: MemberRole.TEACHER,
    roleName: MemberRole.TEACHER
  },
  {
    role: MemberRole.OBSERVER,
    roleName: MemberRole.OBSERVER
  }
];

export default Vue.extend({
  components: {
    GetStartedNav,
    GetStartedNavNextButton,
    PeerGroupRoleInfo,
    RoleSelectorPills
  },

  data() {
    return {
      emailOrEmails: '',
      recentlyAddedEmailCutoff: Date.now(),
      roleConfig: defaultRoleConfig
    };
  },

  computed: {
    manualGroup(): ManualGroupBuilder {
      const groupBuilder = store.state.getStarted.groupBuilder;

      if (groupBuilder.kind !== GroupBuilderKind.MANUAL) {
        throw new Error(
          'Trying to access a builder of the wrong type in the manual group builder'
        );
      }

      return groupBuilder;
    },

    name(): string {
      return this.manualGroup.name;
    },

    nameValidation(): InputValidation.InputLengthValidation {
      return InputValidation.validateInput(this.name, 250, true);
    },

    section(): string {
      return this.manualGroup.section;
    },

    sectionValidation(): InputValidation.InputLengthValidation {
      return InputValidation.validateInput(this.section, 250, false);
    },

    description(): string {
      return this.manualGroup.description;
    },

    descriptionValidation(): InputValidation.InputLengthValidation {
      return InputValidation.validateInput(this.description, 250, false);
    },

    members(): {
      email: string;
      role: MemberRole;
      valid: boolean;
      timestamp: number;
    }[] {
      return this.manualGroup.members;
    },

    recentlyAddedEmails(): { email: string; valid: boolean }[] {
      return this.members
        .filter(m => m.timestamp > this.recentlyAddedEmailCutoff)
        .reverse();
    },

    isEmailValid(): boolean {
      return Utils.validateEmail(this.emailOrEmails);
    },

    areEmailsValid(): boolean {
      return this.parsed.length > 1;
    },

    canAddMember(): boolean {
      return (
        (this.isEmailValid &&
          this.members.every(({ email }) => email !== this.emailOrEmails)) ||
        this.areEmailsValid
      );
    },

    hasName(): boolean {
      return !!this.name;
    },

    allEmailsValid(): boolean {
      return this.members.every(({ valid }) => valid);
    },

    canAdvance(): boolean {
      return (
        this.nameValidation.valid &&
        this.sectionValidation.valid &&
        this.descriptionValidation.valid &&
        this.allEmailsValid
      );
    },

    parsed(): { raw: string; normalized: string }[] {
      return Utils.parseByDelimiter(this.emailOrEmails)
        .map(Utils.trimBrackets)
        .map(raw => ({
          raw,
          normalized: raw.toLowerCase()
        }));
    },

    showMultiUserAdd(): boolean {
      return this.parsed.length > 1;
    },

    errors(): string[] {
      return [
        ...(this.hasName ? [] : ['* Please add a class name']),
        ...(this.allEmailsValid
          ? []
          : ['* Please make sure all emails are valid'])
      ];
    }
  },

  methods: {
    updateName(updatedName: string) {
      store.dispatch.getStarted.updateManualGroup({ name: updatedName });
    },

    updateSection(updatedSection: string) {
      store.dispatch.getStarted.updateManualGroup({
        section: updatedSection
      });
    },

    updateDescription(updatedDescription: string) {
      store.dispatch.getStarted.updateManualGroup({
        description: updatedDescription
      });
    },

    tryAddMember(parsed: { raw: string; normalized: string }) {
      if (this.members.some(m => m.email.toLowerCase() === parsed.normalized)) {
        return;
      }

      store.dispatch.getStarted.updateManualGroup({
        members: [
          // Use the raw email here so that users aren't confused that the emails they entered
          // did not keep their capitalization;
          //
          // We do the final normalization on the server.
          //
          // Prefix the new member, such that the user can see the new members without scrolling
          {
            email: parsed.raw,
            role: MemberRole.STUDENT,
            valid: Utils.validateEmail(parsed.raw),
            timestamp: Date.now()
          },
          ...this.manualGroup.members
        ]
      });
    },

    // We add emails, even if they don't pass validation -- since the user can bulk insert
    // it's a little unreasonable to ask them to find an email in the middle of a long string
    // they just pasted, in order to fix it.
    //
    // They have a chance to see which validate/don't after they've hit "Add (all)".
    async addEmailOrEmails() {
      this.parsed.forEach(this.tryAddMember);

      this.emailOrEmails = '';

      await Utils.sleep(1000);

      this.recentlyAddedEmailCutoff = Date.now();
    },

    removeMember(email: string) {
      store.dispatch.getStarted.updateManualGroup({
        members: this.members.filter(member => member.email !== email)
      });
    },

    updateEmail(index: number, email: string) {
      store.dispatch.getStarted.updateManualGroup({
        members: Object.assign([], this.members, {
          [index]: {
            ...this.members[index],
            email,
            valid: Utils.validateEmail(email)
          }
        })
      });
    },

    updateMember(email: string, role: MemberRole) {
      store.dispatch.getStarted.updateManualGroup({
        members: Utils.updateFirstWhere(
          this.members,
          member => member.email === email,
          ({ valid, timestamp }) => ({ email, role, valid, timestamp })
        )
      });
    },

    clearFilter() {
      this.emailOrEmails = '';
    },

    next() {
      store.dispatch.getStarted.goToStep(
        WorkflowStep.FIND_OR_CREATE_GOALS_BRANCH
      );
    }
  }
});
